import { useQuery } from '@tanstack/react-query';
import { fetchApi } from '@/lib/api/client';
import { billingKeys } from '@/hooks/api/query-keys';

// ============ Types ============

export interface Subscription {
  plan: string;
  tier?: string;
  status: 'active' | 'trialing' | 'past_due' | 'canceled';
  recipientLimit: number | null; // null = unlimited
  workspaceLimit: number | null; // null = unlimited
  seatLimit?: number | null;     // null = unlimited
  price?: number | null;
  currency?: string | null;
  currentPeriodStart?: string | null;
  currentPeriodEnd: string | null;
  trialEndsAt?: string | null;
  createdAt?: string;
}

export interface Usage {
  plan?: string;
  status?: string;
  recipientLimit?: number | null;
  recipientUsage?: number;
  workspaceLimit?: number | null;
  workspaceUsage?: number;
  seatLimit?: number | null;
  seatUsage?: number;
  trialEndsAt?: string | null;
  /** Legacy aliases retained for backward-compatibility */
  recipients?: number;
  workspaces?: number;
  seats?: number;
}

// ============ Query Hooks ============

/**
 * Both queries expose `isError` — and the billing page has to read it.
 *
 * `GET /v1/billing/*` is gated on `read:Billing`, which `buildPermissions`
 * grants to NO workspace role and, since RC-A, to no Company Admin either:
 * only the Company Owner's `manage:all` satisfies it. Every other principal
 * therefore gets a 403 here, and `isLoading` is FALSE once a query has errored
 * — so a page that branches only on `isLoading` renders its fallbacks as if the
 * load had SUCCEEDED. That is how /settings/billing came to show a fabricated
 * "Enterprise" plan with "0 / Unlimited" usage to people who cannot read
 * billing at all.
 *
 * `enabled` exists for the same rule seen from the other side: a query fired
 * only to be refused costs a round trip, a red console entry and a retry storm
 * behind it. The caller decides whether to ask; the server is still the
 * boundary.
 */
export function useSubscription(options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: billingKeys.subscription(),
    queryFn: () => fetchApi<Subscription>('/billing/subscription'),
    enabled: options.enabled ?? true,
  });
}

export function useUsage(options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: billingKeys.usage(),
    queryFn: () => fetchApi<Usage>('/billing/usage'),
    enabled: options.enabled ?? true,
  });
}
